import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import GenericCRUD from "@/components/common/GenericCRUD";
import api from "@/lib/api";
import { resolveMediaUrl } from "@/lib/serviceMedia";
import { bandColor } from "@/components/seo/seoUtils";

function blogThumbSrc(row) {
  const raw = row?.featured_image;
  const src = typeof raw === "string" ? raw : (raw?.src || raw?.url || "");
  return resolveMediaUrl(src) || "";
}

/** Same colored SEO score paint used in SEO Management. */
function SeoScorePaint({ score, pageId, slug }) {
  const color = bandColor(score);
  const label = score == null ? "—" : String(score);
  const href = pageId
    ? `/seo/pages/${pageId}?tab=Overview`
    : "/seo/pages";
  return (
    <Link
      to={href}
      onClick={(e) => e.stopPropagation()}
      title={score == null ? "Not analyzed yet — open SEO Management" : `SEO score ${score} — open in SEO Management`}
      className="inline-flex items-center gap-2 group"
    >
      <span
        className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-xs font-semibold shrink-0 group-hover:border-white/25"
        style={{ color }}
      >
        {label}
      </span>
      <span className="text-[10px] text-white/35 group-hover:text-cyan-200/80 hidden sm:inline">
        {score == null ? "Check" : "SEO"}
      </span>
    </Link>
  );
}

const baseConfig = {
  resource: "blog",
  singular: "article",
  title: "Blog",
  subtitle: "Articles, drafts and scheduled posts.",
  titleKey: "title",
  statusFilter: ["published", "draft"],
  columns: [
    { key: "title", label: "Title", render: (r) => {
      const thumb = blogThumbSrc(r);
      const hasVideo = !!(r.featured_video && String(r.featured_video).trim());
      return (
        <div className="flex items-center gap-3">
          {thumb ? (
            <div className="relative w-10 h-10 shrink-0">
              <img src={thumb} alt="" className="w-10 h-10 rounded object-cover bg-white/5" />
              {hasVideo && (
                <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-black/80 border border-white/20 flex items-center justify-center" title="Has featured video">
                  <Icons.Play className="w-2.5 h-2.5 text-white" />
                </span>
              )}
            </div>
          ) : hasVideo ? (
            <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center text-white/50" title="Featured video">
              <Icons.Video className="w-4 h-4" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center text-white/30 text-xs">No img</div>
          )}
          <span className="text-white font-medium">{r.title}</span>
        </div>
      );
    }},
    { key: "author", label: "Author" },
    { key: "category", label: "Category" },
    {
      key: "seo_score",
      label: "SEO",
      className: "w-28",
      render: (r) => (
        <SeoScorePaint score={r.seo_score} pageId={r.seo_page_id} slug={r.slug} />
      ),
    },
    { key: "reading_time", label: "Read Time", className: "w-24", render: (r) => <span className="text-white/60">{r.reading_time || "—"}</span> },
    { key: "status", label: "Status", badge: true, className: "w-32" },
  ],
  defaultValues: {
    status: "draft",
    category: "",
    tags: [],
    views: 0,
    comments: [],
    excerpt: "",
    featured_image: null,
    featured_video: "",
    reading_time: "",
    published_at: "",
    gallery: [],
    sections: [],
    seo: {
      meta_title: "",
      meta_description: "",
      keywords: [],
      canonical_url: "",
      robots: "index,follow",
      og_title: "",
      og_description: "",
      og_image: "",
      og_type: "article",
      schema: "",
    },
  },
};

function buildFields(categoryOptions) {
  return [
    { key: "title", label: "Title", type: "text", section: "Article", sectionHint: "Main post content readers see on the blog." },
    { key: "slug", label: "URL slug", type: "text", hint: "Appears in /blog/your-slug — leave blank to auto-generate" },
    { key: "featured_image", label: "Featured image", type: "image", folder: "blog", hint: "Shown on cards and as default social image if OG image is empty" },
    { key: "featured_video", label: "Featured video", type: "video", folder: "blog", hint: "Optional MP4/WebM/MOV up to 70MB. Plays on the article page below the hero." },
    { key: "author", label: "Author", type: "text", placeholder: "CAS Media" },
    {
      key: "category",
      label: "Category",
      type: "select",
      options: categoryOptions,
      hint: categoryOptions.length
        ? "Pick from Blog Categories. Manage the list under People & Content → Blog Categories."
        : "No categories yet — add some in Blog Categories first.",
    },
    { key: "excerpt", label: "Excerpt", type: "textarea", rows: 3, hint: "Short summary on blog cards (auto from content if empty)" },
    { key: "tags", label: "Tags", type: "tags", placeholder: "branding, website, seo" },
    { key: "content", label: "Content (rich text)", type: "richtext", placeholder: "Write your article…" },
    { key: "sections", label: "Content sections", type: "sections", hint: "Optional multi-block layout with titles, text, and images" },
    { key: "gallery", label: "Image gallery", type: "gallery", folder: "blog", hint: "Extra images for the article gallery" },
    { key: "reading_time", label: "Reading time", type: "text", hint: "e.g. 5 min read (auto-calculated if empty)" },
    { key: "published_at", label: "Publish date", type: "text", hint: "ISO or human date, e.g. 2026-08-19" },
    {
      key: "status",
      label: "Status",
      type: "choice-row",
      options: [
        { label: "Draft", value: "draft" },
        { label: "Published", value: "published" },
        { label: "Scheduled", value: "scheduled" },
      ],
      hint: "Published posts appear on the live site.",
    },
    {
      key: "seo",
      type: "seo",
      label: "SEO",
      section: "Search & social SEO",
      sectionHint: "Google title, description, social share, and indexing — with draft vs live preview.",
      folder: "blog",
      ogTypeDefault: "article",
      pathFrom: (v) => (v?.slug ? `/blog/${v.slug}` : ""),
    },
  ];
}

export default function Blog() {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/site/blog-categories", { params: { page_size: 100 } });
        if (cancelled) return;
        const items = (data?.items || [])
          .filter((c) => (c.status || "published") !== "draft")
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setCategories(items);
      } catch {
        if (!cancelled) setCategories([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const config = useMemo(() => {
    const options = categories
      .map((c) => {
        const name = String(c.name || "").trim();
        return name ? { label: name, value: name } : null;
      })
      .filter(Boolean);
    return {
      ...baseConfig,
      fields: buildFields(options),
      headerExtra: (
        <Link
          to="/blog-categories"
          className="text-xs text-cyan-200/80 hover:text-cyan-100 underline underline-offset-2"
        >
          Manage categories
        </Link>
      ),
    };
  }, [categories]);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Link
          to="/blog-categories"
          className="text-xs text-cyan-200/80 hover:text-cyan-100 underline underline-offset-2"
        >
          Manage blog categories →
        </Link>
      </div>
      <GenericCRUD config={config} />
    </div>
  );
}
